import {
  Icon,
  type IconName,
  PanelSection,
  type PanelSectionProps,
  Tag,
  Tooltip,
  colors,
} from '@dev-spendesk/grapes';
import cx from 'classnames';
import React, { type ReactNode } from 'react';

type Props = DistributiveOmit<PanelSectionProps, 'children'> & {
  items: {
    label: string;
    value: string;
    icon: IconName;
    variant: 'info' | 'success';
  }[];
  panelActions?: ReactNode;
  inline?: boolean;
};

export function PanelIconItemsSection({
  items,
  panelActions,
  inline = false,
  ...panelSectionProps
}: Props) {
  if (items.length === 0 && !panelActions) {
    return null;
  }

  if (inline) {
    return (
      <PanelSection {...panelSectionProps}>
        <ul className="mt-24 flex items-center gap-8">
          {items.map((item) => (
            <li key={item.label}>
              <Tooltip content={item.value} triggerAsChild>
                <Tag iconName={item.icon} variant={item.variant}>
                  {item.label}
                </Tag>
              </Tooltip>
            </li>
          ))}
        </ul>
        {panelActions}
      </PanelSection>
    );
  }

  return (
    <PanelSection {...panelSectionProps}>
      <ul>
        {items.map((item) => (
          <li key={item.label} className="mb-8 flex justify-between last:mb-0">
            <span
              className={cx('mr-8 flex items-center title-m', {
                'text-content-info-default': item.variant === 'info',
                'text-content-success-default': item.variant === 'success',
              })}
            >
              <Icon
                name={item.icon}
                color={variantToColor(item.variant)}
                className="mr-8"
              />
              {item.label}
            </span>
            <span className="truncate text-content-secondary-bg-primary body-m">
              {item.value}
            </span>
          </li>
        ))}
      </ul>
      {panelActions}
    </PanelSection>
  );
}

const variantToColor = (variant: Props['items'][number]['variant']) => {
  if (variant === 'success') {
    return colors.contentSuccessDefault;
  }
  return colors.contentInfoDefault;
};
